import React from 'react';
import {
  useActivityLearnScroll,
  getActivityLessonHeaders,
  ACTIVITY_LEARN_SECTIONS,
} from '../../hooks/use-activity-lesson-scroll-state';
import {
  ActivityContext,
  ActivityContextLLMPlaygroundQuestionAnswer,
  ActivityContextType,
} from '../../contexts/activity';
import { ACTIVITY_STEP } from './Content';
import CyclingIcons from '../CyclingIcons';
import {
  AIIcon,
  CheckedIcon,
  ClickableRow,
  Divider,
  HeaderRow,
  Label,
  Section,
  SideNavigationWrapper,
  Text,
  LeftToggleButton,
  UncheckedIcon,
} from '../Navbar/SideBar';
import { ReviewIcon } from '../Navbar/SideBar';
import { IconsRow } from '../Navbar/SideBar';

const QuestionIndicators = ({
  activity,
  answer,
  questionIndex,
  isActive,
  isCorrect,
  doesSatisfyAllRequirements,
}: {
  activity: ActivityContextType;
  answer: ActivityContextType['submission']['questionSlugToAnswer'][string];
  questionIndex: number;
  isActive: boolean;
  isCorrect: boolean | null;
  doesSatisfyAllRequirements: boolean | null;
}) => {
  const question = activity?.exercise?.questions?.[questionIndex];
  const hasMentorComment =
    !!activity.latestSavedSubmission?.mentor_reviews?.find(
      (review) => review.questionId === question.id
    )?.review;
  // TODO: we should also save correctness state so we shoudln't need to run all sql queries to figure out if the answer is correct
  return (
    <IconsRow>
      {hasMentorComment && <ReviewIcon xOffset={6} />}
      {answer.isAnswered ? (
        <CheckedIcon
          isActive={isActive}
          isCorrect={isCorrect !== false}
          doesSatisfyAllRequirements={doesSatisfyAllRequirements}
        />
      ) : (
        <UncheckedIcon isActive={isActive} />
      )}
    </IconsRow>
  );
};

const ActivityNavigation = ({
  onClose,
  submitAndMoveToQuestion,
  submitAndMoveToReview,
  submitAndMoveToLesson,
  isSubmitting,
  type,
}: {
  onClose: () => void;
  submitAndMoveToQuestion: ({
    questionIndex,
    questionSlug,
  }: {
    questionIndex: number;
    questionSlug: string;
  }) => Promise<void>;
  submitAndMoveToReview: () => Promise<void>;
  submitAndMoveToLesson: () => Promise<void>;
  isSubmitting: boolean;
  type: string;
}) => {
  // A preview is for inspecting content, not attempting it, so the
  // "you haven't tried this yet" nudges would fire on every step.
  const isPreview = type === 'PREVIEW';
  const [activity] = React.useContext(ActivityContext);
  const lessonHeaders = getActivityLessonHeaders(activity.exercise.lesson);
  const questions = activity.exercise.questions;

  const { activeSection, scrollTo } = useActivityLearnScroll(lessonHeaders);
  const [activeStep, setActiveStep] = React.useState(activity.currentStep);

  const hasOverallMentorFeedback =
    !!activity.mentorReview.review || !!activity.mentorReview.score;
  const hasAnsweredAllQuestions = questions.every(
    (question) =>
      activity.submission.questionSlugToAnswer[question.slug].isAnswered
  );
  const hasAIReview = activity.latestSavedSubmission?.ai_review;

  const currentQuestionAnswer =
    activity.currentStep?.type === ACTIVITY_STEP.QUESTION
      ? activity.submission.questionSlugToAnswer[
          (
            activity.currentStep as {
              type: ACTIVITY_STEP.QUESTION;
              questionSlug: string;
            }
          ).questionSlug
        ]
      : null;

  React.useEffect(() => {
    setActiveStep(activity.currentStep);
  }, [activity.currentStep]);

  const canMoveFromQuestion = () => {
    if (isPreview) {
      return true;
    }

    if (activeStep?.type !== ACTIVITY_STEP.QUESTION) {
      return true;
    }

    if (currentQuestionAnswer?.isAnswered) {
      return true;
    }

    return window.confirm(
      "You haven't tried this exercise yet. Are you sure you want to move on?"
    );
  };

  const canMoveToReview = () => {
    if (isPreview) {
      return true;
    }

    if (activeStep?.type === ACTIVITY_STEP.FEEDBACK) {
      return true;
    }

    if (hasAIReview) {
      return true;
    }

    if (hasAnsweredAllQuestions) {
      return true;
    }

    return window.confirm(
      'We recommend attempting all exercises before moving to the review. Do you want to continue?'
    );
  };

  const goToLessonSection = async (header?: string) => {
    if (canMoveFromQuestion()) {
      setActiveStep({
        type: ACTIVITY_STEP.LESSON,
      });
      await submitAndMoveToLesson();
      return setTimeout(
        () => {
          return scrollTo(header);
        },
        activeStep?.type === ACTIVITY_STEP.LESSON ? 0 : 500
      );
    }
  };

  const goToQuestion = async (questionIndex: number) => {
    if (canMoveFromQuestion()) {
      const questionSlug = questions[questionIndex].slug;
      setActiveStep({
        type: ACTIVITY_STEP.QUESTION,
        questionIndex,
        questionSlug,
      });
      await submitAndMoveToQuestion({ questionIndex, questionSlug });
    }
  };

  const goToReview = async () => {
    if (canMoveToReview()) {
      setActiveStep({
        type: ACTIVITY_STEP.FEEDBACK,
      });
      await submitAndMoveToReview();
    }
  };

  const shouldLockReview = false;

  return (
    <SideNavigationWrapper>
      <HeaderRow>
        <Text style={{ fontWeight: 'bold' }}>{activity.content_title}</Text>
        <LeftToggleButton onClick={onClose} />
      </HeaderRow>
      <Divider />
      <Section>
        <Label>Intro</Label>
        <ClickableRow
          onClick={() => goToLessonSection()}
          isActive={
            activeStep?.type === ACTIVITY_STEP.LESSON &&
            activeSection === ACTIVITY_LEARN_SECTIONS.TITLE.getSection()
          }
          isLoading={isSubmitting}
        >
          <Text>{activity.title}</Text>
        </ClickableRow>
        {lessonHeaders.map((header) => {
          return (
            <ClickableRow
              key={`activity-navigation-header-${header}`}
              onClick={() => goToLessonSection(header)}
              isLoading={isSubmitting}
              isActive={
                activeStep?.type === ACTIVITY_STEP.LESSON &&
                activeSection ===
                  ACTIVITY_LEARN_SECTIONS.HEADER.getSection(header)
              }
            >
              <Text>{header}</Text>
            </ClickableRow>
          );
        })}
      </Section>
      <Divider />
      <Section>
        <Label>Exercises</Label>
        {questions.map((question, index) => {
          const isLocked = false;

          return (
            <ClickableRow
              key={`activity-navigation-question-${index}`}
              onClick={isLocked ? () => {} : () => goToQuestion(index)}
              isActive={
                activeStep?.type === ACTIVITY_STEP.QUESTION &&
                activeStep?.questionIndex === index
              }
              isLoading={isSubmitting}
              isDisabled={isLocked}
            >
              <Text>Exercise {index + 1}</Text>
              <QuestionIndicators
                activity={activity}
                questionIndex={index}
                answer={activity.submission.questionSlugToAnswer[question.slug]}
                isActive={
                  activeStep?.type === ACTIVITY_STEP.QUESTION &&
                  activeStep?.questionIndex === index
                }
                isCorrect={
                  activity.submission.questionSlugToAnswer[question.slug]
                    .isCorrect
                }
                doesSatisfyAllRequirements={
                  (
                    activity.submission.questionSlugToAnswer[
                      question.slug
                    ] as ActivityContextLLMPlaygroundQuestionAnswer
                  ).doesSatisfyAllRequirements !== false
                }
              />
            </ClickableRow>
          );
        })}
      </Section>
      <Divider />
      <Section>
        <ClickableRow
          onClick={shouldLockReview ? () => {} : goToReview}
          isActive={activeStep?.type === ACTIVITY_STEP.FEEDBACK}
          isLoading={isSubmitting}
          isDisabled={shouldLockReview}
        >
          <Text>Review and Continue</Text>
          <IconsRow>
            {hasOverallMentorFeedback ? (
              <CyclingIcons icons={[<ReviewIcon xOffset={0} />, <AIIcon />]} />
            ) : (
              <AIIcon />
            )}
          </IconsRow>
        </ClickableRow>
      </Section>
    </SideNavigationWrapper>
  );
};

export default ActivityNavigation;
